<script setup lang="ts">
import {
    AlertCircle,
    BookOpen,
    Bot,
    Database,
    FileText,
    FolderKanban,
    Loader2,
    Minus,
    Presentation,
    TrendingDown,
    TrendingUp
} from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import {
    type AnalyticsDashboard,
    type AnalyticsPeriod,
    getAnalytics,
    type LabeledCount,
    type Metric
} from '@/services/analytics';
import { useTenantStore } from '@/stores/tenant';

import AnalyticsDeliverableTile from './analytics/AnalyticsDeliverableTile.vue';
import AnalyticsKpiCard from './analytics/AnalyticsKpiCard.vue';
import AnalyticsModelUsageCard from './analytics/AnalyticsModelUsageCard.vue';
import AnalyticsToolFamilyCard from './analytics/AnalyticsToolFamilyCard.vue';
import { formatDecimal, formatInt } from './analytics/analyticsUtils';

const PERIODS: { value: AnalyticsPeriod; label: string }[] = [
    { value: '7d', label: 'Last 7 days' },
    { value: '1m', label: 'Last month' },
    { value: '3m', label: 'Last 3 months' },
    { value: '6m', label: 'Last 6 months' },
    { value: '1y', label: 'Last year' }
];

const FAMILY_LABELS: Record<string, string> = {
    attached_files: 'Attached files',
    data_sources: 'Data sources',
    web: 'Web search',
    slide_studio: 'Slide studio',
    doc_studio: 'Doc studio',
    pure_llm: 'No tool (model only)',
    other: 'Other tools'
};

const tenantStore = useTenantStore();

const period = ref<AnalyticsPeriod>('1m');
const dashboard = ref<AnalyticsDashboard | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

const isElqano = computed(() => tenantStore.tenantElqano);

watch(
    () => [tenantStore.tenantId, period.value] as const,
    async ([tenantId, selectedPeriod], _, onCleanup) => {
        if (!tenantId) return;

        // A newer selection cancels this request: its late answer must not overwrite the current one.
        const controller = new AbortController();
        onCleanup(() => controller.abort());

        loading.value = true;
        error.value = null;
        try {
            const result = await getAnalytics(
                tenantId,
                selectedPeriod,
                controller.signal
            );
            if (!controller.signal.aborted) dashboard.value = result;
        } catch (err) {
            if (controller.signal.aborted) return;
            error.value =
                err instanceof Error ? err.message : 'Failed to load analytics';
            dashboard.value = null;
        } finally {
            if (!controller.signal.aborted) loading.value = false;
        }
    },
    { immediate: true }
);

const byLabel = (rows: LabeledCount[]): Record<string, number> =>
    Object.fromEntries(rows.map((row) => [row.label, row.count]));

const delta = (metric: Metric) => {
    if (metric.delta_pct === null) {
        return {
            class: 'bg-grey20 text-muted-foreground',
            icon: Minus,
            text: 'n/a'
        };
    }
    if (metric.delta_pct > 0) {
        return {
            class: 'bg-emerald-500/10 text-emerald-600',
            icon: TrendingUp,
            text: `+${formatDecimal(metric.delta_pct)}%`
        };
    }
    if (metric.delta_pct < 0) {
        return {
            class: 'bg-destructive/10 text-destructive',
            icon: TrendingDown,
            text: `${formatDecimal(metric.delta_pct)}%`
        };
    }
    return {
        class: 'bg-grey20 text-muted-foreground',
        icon: Minus,
        text: '0%'
    };
};

const families = computed(() => {
    const tools = dashboard.value?.tools;
    if (!tools) return [];
    return [...tools.families]
        .sort((a, b) => b.messages - a.messages)
        .map((family) => ({
            label: FAMILY_LABELS[family.family] ?? family.family,
            count: family.messages,
            pct: family.pct,
            width: family.pct
        }));
});

const displayModelLabel = (modelId: string, modelLabel: string) => {
    const label = modelLabel.trim();
    if (label && label !== modelId) return label;

    return modelId
        .replace(/^(?:[a-z]{2}\.)?anthropic\./i, '')
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
        .replace(/\bGpt\b/g, 'GPT')
        .replace(/\b(Sonnet|Opus|Haiku) (\d+) (\d+)\b/g, '$1 $2.$3');
};

const models = computed(() => {
    const rows = dashboard.value?.models ?? [];
    const total = rows.reduce((sum, row) => sum + row.count, 0);
    return rows.map((row) => {
        const id = 'model_id' in row ? row.model_id : row.label;
        const label = 'model_label' in row ? row.model_label : row.label;
        return {
            id,
            label: displayModelLabel(id, label),
            count: row.count,
            pct: total ? Math.round((row.count / total) * 1000) / 10 : 0
        };
    });
});

const toolsFootnote = computed(() => {
    const captured = dashboard.value?.tools.total_messages ?? 0;
    return (
        `${formatInt(captured)} ${captured === 1 ? 'run' : 'runs'} captured in this period. ` +
        'Tracking began at deployment, so this total may be lower than the message count above.'
    );
});

const slideStudioRows = computed(() => {
    const block = dashboard.value?.slide_studio;
    if (!block) return [];
    const themes = byLabel(block.themes_created);
    return [
        { label: 'Presentations created', count: block.presentations_created },
        { label: 'Slides generated', count: block.slides_generated },
        {
            label: 'Presentations exported',
            count: block.presentations_exported
        },
        { label: 'Public themes', count: themes.public ?? 0 },
        { label: 'Private themes', count: themes.private ?? 0 },
        { label: 'PPTX imported', count: block.library_pptx_imported }
    ];
});

const docStudioRows = computed(() => {
    const block = dashboard.value?.doc_studio;
    if (!block) return [];
    return [
        { label: 'Documents created', count: block.documents_created },
        { label: 'Documents exported', count: block.documents_exported }
    ];
});

const knowledgeRows = computed(() => {
    const block = dashboard.value?.documents;
    if (!block) return [];
    return [
        { label: 'Documents indexed', count: block.documents_indexed },
        { label: 'Pages indexed', count: block.pages_indexed },
        { label: 'Files attached to a chat', count: block.attached_files }
    ];
});

const dataSourceRows = computed(() => {
    const block = dashboard.value?.documents;
    if (!block) return [];
    const created = byLabel(block.data_sources_created);
    return [
        { label: 'Public', count: created.public ?? 0 },
        { label: 'Private', count: created.private ?? 0 },
        { label: 'Shared', count: created.shared ?? 0 }
    ];
});

const customAgentRows = computed(() => {
    const block = dashboard.value?.custom_agents;
    if (!block) return [];
    const created = byLabel(block.created);
    return [
        { label: 'Created — private', count: created.private ?? 0 },
        { label: 'Created — public', count: created.public ?? 0 },
        { label: 'Created — shared', count: created.shared ?? 0 },
        { label: 'Used over the period', count: block.used }
    ];
});

const projectRows = computed(() => {
    const block = dashboard.value?.projects;
    if (!block) return [];
    return [
        { label: 'Projects created', count: block.projects_created },
        {
            label: 'Messages per active project',
            count: Math.round(block.messages_per_project)
        }
    ];
});
</script>

<template>
    <div class="analytics-dashboard flex min-h-full min-w-0 flex-col gap-6">
        <div class="flex flex-wrap items-start justify-between gap-4">
            <div class="flex flex-col gap-2">
                <h2 class="text-xl font-semibold">Analytics</h2>
                <p class="text-sm text-muted-foreground">
                    {{
                        isElqano
                            ? 'Usage and activity metrics across all client tenants.'
                            : 'Usage and activity metrics for your workspace.'
                    }}
                </p>
            </div>
            <div class="analytics-filters flex flex-wrap items-center gap-2">
                <Select v-model="period">
                    <SelectTrigger class="min-w-0 w-[170px] max-w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem
                            v-for="option in PERIODS"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>

        <div
            v-if="error"
            class="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
            <AlertCircle class="size-4 shrink-0" />
            {{ error }}
        </div>

        <div
            v-if="loading && !dashboard"
            class="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground"
        >
            <Loader2 class="size-4 animate-spin" />
            Loading analytics…
        </div>

        <template v-else-if="dashboard">
            <div class="analytics-kpis grid grid-cols-1 gap-4">
                <AnalyticsKpiCard
                    label="Messages"
                    :value="formatInt(dashboard.usage.total_messages.value)"
                    caption="Messages sent over the period"
                    :delta="delta(dashboard.usage.total_messages)"
                />
                <AnalyticsKpiCard
                    label="Active users"
                    :value="formatInt(dashboard.usage.active_users.value)"
                    caption="Sent at least one message"
                    :delta="delta(dashboard.usage.active_users)"
                />
                <AnalyticsKpiCard
                    label="Messages per user"
                    :value="
                        formatDecimal(dashboard.usage.messages_per_user.value)
                    "
                    caption="Average over active users"
                    :delta="delta(dashboard.usage.messages_per_user)"
                />
                <AnalyticsKpiCard
                    label="Top 10% most active"
                    :value="
                        formatInt(dashboard.usage.top_decile_threshold.value)
                    "
                    :caption="`At least this many messages — ${formatDecimal(dashboard.usage.top_decile_avg.value)} on average`"
                    :delta="delta(dashboard.usage.top_decile_threshold)"
                />
            </div>

            <div class="analytics-insights grid grid-cols-1 gap-4">
                <AnalyticsToolFamilyCard
                    title="Tool usage"
                    description="Tool types ranked by use across captured runs. One run may use several types, so the percentages can total over 100%."
                    :meta-label="`${formatInt(dashboard.tools.total_messages)} runs`"
                    :rows="families"
                    :footnote="toolsFootnote"
                />
                <AnalyticsModelUsageCard
                    title="AI models"
                    description="Messages answered by each model."
                    :meta-label="`${models.length} models`"
                    :rows="models"
                />
            </div>

            <div class="analytics-deliverables grid grid-cols-1 gap-4">
                <AnalyticsDeliverableTile
                    title="Slide studio"
                    :icon="Presentation"
                    badge="bg-primary20 text-primary"
                    :rows="slideStudioRows"
                />
                <AnalyticsDeliverableTile
                    title="Doc studio"
                    :icon="FileText"
                    badge="bg-primary20 text-primary"
                    :rows="docStudioRows"
                />
                <AnalyticsDeliverableTile
                    title="Knowledge base"
                    :icon="BookOpen"
                    badge="bg-primary20 text-primary"
                    :rows="knowledgeRows"
                />
                <AnalyticsDeliverableTile
                    title="Data sources created"
                    :icon="Database"
                    badge="bg-primary20 text-primary"
                    :rows="dataSourceRows"
                />
                <AnalyticsDeliverableTile
                    title="Custom agents"
                    :icon="Bot"
                    badge="bg-primary20 text-primary"
                    :rows="customAgentRows"
                />
                <AnalyticsDeliverableTile
                    title="Projects"
                    :icon="FolderKanban"
                    badge="bg-primary20 text-primary"
                    :rows="projectRows"
                />
            </div>
        </template>
    </div>
</template>

<style scoped>
.analytics-dashboard {
    container-type: inline-size;
}

@container (min-width: 580px) {
    .analytics-kpis,
    .analytics-deliverables {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@container (min-width: 720px) {
    .analytics-insights {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        align-items: start;
    }
}

@container (min-width: 960px) {
    .analytics-kpis {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .analytics-deliverables {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@container (max-width: 430px) {
    .analytics-filters,
    .analytics-filters :deep(button) {
        width: 100%;
    }
}
</style>
